import {
  Box,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { ErrorMessage, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import {
  getDropdownCity,
  getDropdownState,
  getDropdownZipCode,
  getStorageValue,
  setStorageValue
} from "../../utils/helper";

const DeliveryAddressForm = ({
  handleChange,
  values,
  touched,
  errors,
  setFieldTouched,
  setFieldValue
}) => {
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [zipcodeSearchTerm, setZipcodeSearchTerm] = useState("");

  const { isSubmitting, isValidating } = useFormikContext();
  
    useEffect(() => {
      if (isSubmitting && !isValidating) {
        let keys = Object.keys(errors);
        if (keys.length > 0) {
          const selector = `[name=${keys[0]}]`;
          const errorElement = document.querySelector(selector);
          if (errorElement) {
            errorElement.focus();
          }
        }
      }
    }, [errors, isSubmitting, isValidating]);

  useEffect(() => {
    setStorageValue("fieldValues", JSON.stringify(values));
  }, [values])

  useEffect(() => {
    setdropdownState();

    if (values.deliveryState && values.deliveryCity && values.deliveryZipcode) {
      setdropdownCity(values.deliveryState);
      setdropdownZipcode(values.deliveryState, values.deliveryCity);
    }
    // eslint-disable-next-line
  }, [values.deliveryState]);

  /**
   * fetches dropdowns values of state from api
   */
  const setdropdownState = async () => {
    const response = await getDropdownState();
    setStates(response.data?.state);
  };

  /**
   * fetches dropdowns values of city from api
   * @param {string} state - chosen state
   */
  const setdropdownCity = async (state) => {
    const response = await getDropdownCity(state);
    setCities(response.data?.city);
    if (response.data?.city.length === 1) {
      handleChange({
        target: { name: "deliveryCity", value: response.data?.city[0] },
      });
      setdropdownZipcode(state, response.data?.city[0]);
    }
  };

  /**
   * fetches dropdowns values of zipCodes from api
   * @param {string} state - chosen state
   * @param {*} city - chosen city
   */
  const setdropdownZipcode = async (state, city) => {
    const result = await getDropdownZipCode(state, city);
    setFieldTouched("deliveryZipcode", false);
    setZipCodes(result?.data?.zipCode);
    const updatedValues = JSON.parse(getStorageValue("fieldValues"));
    if (result?.data?.zipCode?.length === 1 && updatedValues?.deliveryState) {
      handleChange({
        target: { name: "deliveryZipcode", value: result?.data?.zipCode[0] },
      });
    }
  };

  /**
   * handle state dropdown search
   * @param {*} event
   */
  const handleStateSearchChange = (event) => {
    setStateSearchTerm(
      event.target.value?.replace(/[^a-zA-Z ]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart()
    );
  };

  /**
   * handle zipcode dropdown search
   * @param {*} event
   */
  const handleZipcodeSearchChange = (event) => {
    setZipcodeSearchTerm(event.target.value?.replace(/\s\s+/g, ' ')?.replace(/[^0-9]+/ig, ""));
  };

  // Filtered options based on search term
  const filteredZipcodes = zipCodes?.filter((zipValue) =>
    zipValue?.toLowerCase().startsWith(zipcodeSearchTerm?.toLowerCase())
  );

  // Filtered options based on search term
  const filteredStates = states?.filter(
    (state) =>
      state.state.toLowerCase().startsWith(stateSearchTerm.toLowerCase()) ||
      state.stateCode.toLowerCase().startsWith(stateSearchTerm.toLowerCase())
  );

  /**
   * handle city dropdown search
   * @param {*} event
   */
  const handleCitySearchChange = (event) => {
    setFieldTouched("deliveryZipcode", false);
    setCitySearchTerm(
      event.target.value?.replace(/[^a-zA-Z ]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart()
    );
  };

  // Filtered options based on search term
  const filteredCities = cities?.filter((city) =>
    city.toLowerCase().startsWith(citySearchTerm.toLowerCase())
  );

  const handleStateChange = (state) => {
    setFieldTouched("deliveryState", false)
    handleChange({
      target: { name: "deliveryCity", value: "" },
    });
    handleChange({
      target: { name: "deliveryZipcode", value: "" },
    });
    setdropdownCity(state);
  };

  const handleCityChange = (state, city) => {
    setFieldTouched("deliveryZipcode", false);
    handleChange({
      target: { name: "deliveryZipcode", value: "" },
    });
    setdropdownZipcode(state, city);
  };

  const handleDeliveryInfo = (isChecked) => {
    const userProfile = JSON?.parse(getStorageValue('userInfo')) ;
    setFieldValue("deliveryFullName", isChecked ? userProfile?.firstName + " " + userProfile?.lastName : "");
    setFieldValue("deliveryAddress", isChecked ? userProfile?.address?.street : "");
    setFieldValue("deliveryPhoneNumber", isChecked ? userProfile?.phoneNumber : "");
    setFieldValue("deliveryCity", isChecked ? userProfile?.address?.city : "");
    setFieldValue("deliveryState", isChecked ? userProfile?.address?.state : "");
    setFieldValue("deliveryZipcode", isChecked ? userProfile?.address?.zipCode : "");
    setFieldValue("autofillDeliveryAddress", isChecked);

    setFieldTouched("deliveryFullName", false);
    setFieldTouched("deliveryAddress", false);
    setFieldTouched("deliveryPhoneNumber", false);
    setFieldTouched("deliveryCity", false);
    setFieldTouched("deliveryState", false);
    setFieldTouched("deliveryZipcode", false);
  }

  return (
    <>
      <div>
        <Typography sx={{ mt: 2, mb: 1 }} variant="h6">
          Delivery Address:
        </Typography>
        <FormControlLabel
          label="Use my profile address"
          sx={{ paddingBottom: "10px", color: '#344054' }}
          slotProps={{
            typography: {
              fontFamily: "poppins !important",
              fontSize: "15px",
              lineHeight: 1,
              fontWeight: 500
            }
          }}
          control={
            <Checkbox
              name="autofillDeliveryAddress"
              size="small"
              checked={values?.autofillDeliveryAddress}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "autofillDeliveryAddress",
                    value: e?.target?.checked
                  },
                })
                handleDeliveryInfo(e?.target?.checked);
              }
              }
            />
          }
        />

        <Grid2 container>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
              className="form-label required"
            >
              Full name
            </Typography>
            <Grid2>
              <TextField
                sx={{ borderRadius: "8px" }}
                name="deliveryFullName"
                fullWidth
                variant="outlined"
                size="small"
                type="text"
                placeholder="Enter Full Name"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "deliveryFullName",
                      value: e?.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart(),
                    },
                  })
                }
                slotProps={{ htmlInput: { maxLength: 50 } }}
                value={values.deliveryFullName}
                error={
                  touched.deliveryFullName && Boolean(errors.deliveryFullName)
                }
              />
              <ErrorMessage
                name="deliveryFullName"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              className="form-label required"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
            >
              Phone number
            </Typography>
            <Grid2 size={{ xs: 12, sm: 12, md: 5, lg: 5 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  sx={{ borderRadius: "8px", color: "#667085" }}
                  variant="outlined"
                  size="small"
                  type="text"
                  value="+1"
                  className="country-code"
                />
                <TextField
                  name="deliveryPhoneNumber"
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Phone number"
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "deliveryPhoneNumber",
                        value: e?.target?.value?.replace(/\D/g, "")
                      },
                    })
                  }
                  value={values.deliveryPhoneNumber}
                  onInput={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value))
                      .toString()
                      .slice(0, 10);
                  }}
                  maxLength={10}
                  error={
                    touched.deliveryPhoneNumber &&
                    Boolean(errors.deliveryPhoneNumber)
                  }
                />
              </Box>
              <ErrorMessage
                name="deliveryPhoneNumber"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              className="form-label required"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
            >
              Address
            </Typography>
            <Grid2>
              <TextField
                name="deliveryAddress"
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter Address here"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "deliveryAddress",
                      value: e?.target?.value?.replace(/[^a-zA-Z0-9\s.,#\-/;'"_&]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart(),
                    },
                  })
                }
                value={values.deliveryAddress}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                error={
                  touched.deliveryAddress && Boolean(errors.deliveryAddress)
                }
              />
              <ErrorMessage
                name="deliveryAddress"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            container
            spacing={2}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                className="form-label required"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
              >
                State
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="deliveryState"
                  value={values?.deliveryState}
                  onChange={(event) => {
                    handleChange(event);
                    handleStateChange(
                      event.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart()
                    );
                  }}
                  MenuProps={{autoFocus: false}}
                  displayEmpty
                  onOpen={() => {
                    setStateSearchTerm("")
                  }}
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select state</span>
                    )
                  }
                  error={touched.deliveryState && Boolean(errors.deliveryState)}
                >
                  <MenuItem
                    className="dropdown-search"
                    disableRipple
                    sx={{ background: "transparent" }}
                    value={values.deliveryState}
                  >
                    <TextField
                      placeholder="Search state"
                      size="small"
                      sx={{ 
                        outline: "none", 
                        border: "none",
                        "& fieldset": { border: 'none' },
                      }}
                      fullWidth
                      value={stateSearchTerm}
                      slotProps={{htmlInput: {maxLength: 30}}}
                      onChange={handleStateSearchChange}
                      autoFocus
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key !== "Escape") {
                          event.stopPropagation();
                        }
                      }}
                    />
                  </MenuItem>
                  {filteredStates?.length > 0 ? (
                    filteredStates?.map((state) => (
                      <MenuItem key={state.state} value={state.state}>
                        {state.state}
                      </MenuItem>
                    ))
                  ) : filteredStates?.length === 0 && values?.deliveryState ? (
                    <MenuItem value={values?.deliveryState}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>
              <ErrorMessage
                name="deliveryState"
                component="div"
                className="error-messages"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                className="form-label required"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
              >
                City
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="deliveryCity"
                  value={values?.deliveryCity}
                  MenuProps={{autoFocus: false}}
                  onOpen={() => {
                    setCitySearchTerm("")
                  }}
                  onChange={(event) => {
                    handleChange(event);
                    handleCityChange(
                      values?.deliveryState,
                      event.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart()
                    );
                  }}
                  disabled={!values?.deliveryState}
                  sx={{
                    "&.MuiOutlinedInput-root.Mui-disabled": {
                      "& fieldset": {
                        borderColor: touched.deliveryCity && Boolean(errors.deliveryCity) ? "#d32f2f" : "#d2d7df"
                      },
                    }
                  }}
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select city</span>
                    )
                  }
                  error={touched.deliveryCity && Boolean(errors.deliveryCity)}
                >
                  {values?.deliveryState && (
                    <MenuItem
                      className="dropdown-search"
                      disableRipple
                      sx={{ background: "transparent" }}
                      value={values.deliveryCity}
                    >
                      <TextField
                        placeholder="Search City"
                        size="small"
                        sx={{ 
                          outline: "none", 
                          border: "none",
                          "& fieldset": { border: 'none' },
                        }}
                        fullWidth
                        value={citySearchTerm}
                        slotProps={{htmlInput: {maxLength: 30}}}
                        onChange={handleCitySearchChange}
                        autoFocus
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          if (event.key !== "Escape") {
                            event.stopPropagation();
                          }
                        }}
                      />
                    </MenuItem>
                  )}
                  {filteredCities?.length > 0 && values?.deliveryState ? (
                    filteredCities?.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))
                  ) : filteredCities?.length === 0 && values?.deliveryCity ? (
                    <MenuItem value={values?.deliveryCity}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>

              <ErrorMessage
                name="deliveryCity"
                component="div"
                className="error-messages"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                className="form-label required"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
              >
                Zipcode
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="deliveryZipcode"
                  value={values?.deliveryZipcode}
                  MenuProps={{autoFocus: false}}
                  onOpen={() => {
                    setZipcodeSearchTerm("")
                  }}
                  onChange={(event) => {
                    handleChange(event);
                    setFieldTouched("deliveryZipcode", false);
                  }}
                  disabled={!values?.deliveryState || !values?.deliveryCity}
                  displayEmpty
                  sx={{
                    "&.MuiOutlinedInput-root.Mui-disabled": {
                      "& fieldset": {
                        borderColor: touched.deliveryZipcode && Boolean(errors.deliveryZipcode) ? "#d32f2f" : "#d2d7df"
                      },
                    }
                  }}
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select zipcode</span>
                    )
                  }
                  error={
                    touched.deliveryZipcode && Boolean(errors.deliveryZipcode)
                  }
                >
                  {(
                    <MenuItem
                      className="dropdown-search"
                      disableRipple
                      sx={{ background: "transparent" }}
                      value={values.deliveryZipcode}
                    >
                      <TextField
                        placeholder="Search Zipcode"
                        size="small"
                        sx={{ 
                          outline: "none", 
                          border: "none",
                          "& fieldset": { border: 'none' },
                        }}
                        fullWidth
                        value={zipcodeSearchTerm}
                        slotProps={{htmlInput: {maxLength: 30}}}
                        onChange={handleZipcodeSearchChange}
                        autoFocus
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          if (event.key !== "Escape") {
                            event.stopPropagation();
                          }
                        }} 
                      />
                    </MenuItem>
                  )}
                  {values?.deliveryCity ? (
                    filteredZipcodes?.map((zipCode) => (
                      <MenuItem key={zipCode} value={zipCode}>
                        {zipCode}
                      </MenuItem>
                    ))
                  ) : filteredZipcodes?.length === 0 && values?.deliveryZipcode ? (
                    <MenuItem value={values?.deliveryZipcode}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>

              <ErrorMessage
                name="deliveryZipcode"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </div>
    </>
  );
};

export default DeliveryAddressForm;
